import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft,
  User,
  FolderOpen,
  FileCheck,
  Clock,
  CheckCircle2,
  AlertCircle,
  Calendar,
  TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberStatistics = ({ user }) => {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMemberStats();
  }, [memberId, user]);

  const fetchMemberStats = async () => {
    try {
      const response = await axios.get(
        `${API}/team/members/${memberId}/statistics?user_id=${user.user_id}`
      );
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching member stats:', err);
      setError(err.response?.data?.detail || 'Σφάλμα φόρτωσης στατιστικών');
      toast.error('Σφάλμα φόρτωσης στατιστικών');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('el-GR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
          <CheckCircle2 className="h-3 w-3" />
          Ολοκληρώθηκε
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Clock className="h-3 w-3" />
        Σε εξέλιξη
      </span>
    );
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-emerald-500';
    if (progress >= 50) return 'bg-cyan-500';
    if (progress >= 25) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-cyan-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-rose-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Σφάλμα</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Επιστροφή
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-slate-600"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Πίσω
            </Button>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-cyan-100 flex items-center justify-center">
                <User className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">
                  {stats?.member?.full_name || stats?.member?.email}
                </h1>
                <p className="text-sm text-slate-500">{stats?.member?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <FolderOpen className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.summary?.total_projects || 0}</p>
                <p className="text-sm text-slate-500">Συνολικά Έργα</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {(stats?.summary?.app_in_progress || 0) + (stats?.summary?.impl_in_progress || 0)}
                </p>
                <p className="text-sm text-slate-500">Σε Εξέλιξη</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {(stats?.summary?.app_completed || 0) + (stats?.summary?.impl_completed || 0)}
                </p>
                <p className="text-sm text-slate-500">Ολοκληρωμένα</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.summary?.average_progress || 0}%</p>
                <p className="text-sm text-slate-500">Μέση Πρόοδος</p>
              </div>
            </div>
          </div>
        </div>

        {/* Application Projects */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-cyan-600" />
              Έργα Αιτήσεων ({stats?.summary?.total_app_projects || 0})
            </h2>
          </div>
          <div className="divide-y divide-slate-100">
            {stats?.application_projects?.length > 0 ? (
              stats.application_projects.map((project) => (
                <div 
                  key={project.project_id} 
                  className="px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/projects/${project.project_id}`)}
                  data-testid={`app-project-${project.project_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-medium text-slate-900">{project.project_title}</h3>
                      <p className="text-sm text-slate-500">{project.client_name}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {project.deadline && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(project.deadline)}
                        </span>
                      )}
                      {getStatusBadge(project.status)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressColor(project.progress)} transition-all`}
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-slate-600 min-w-[80px] text-right">
                      {project.uploaded_documents}/{project.total_documents} ({project.progress}%)
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-slate-500">
                <FolderOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>Δεν υπάρχουν ανατεθειμένα έργα αιτήσεων</p>
              </div>
            )}
          </div>
        </div>

        {/* Implementation Projects */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-emerald-600" />
              Έργα Υλοποίησης ({stats?.summary?.total_impl_projects || 0})
            </h2>
          </div>
          <div className="divide-y divide-slate-100">
            {stats?.implementation_projects?.length > 0 ? (
              stats.implementation_projects.map((project) => (
                <div 
                  key={project.project_id} 
                  className="px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/implementation/projects/${project.project_id}`)}
                  data-testid={`impl-project-${project.project_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-medium text-slate-900">{project.project_title}</h3>
                      <p className="text-sm text-slate-500">{project.client_name}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {project.deadline && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(project.deadline)}
                        </span>
                      )}
                      {getStatusBadge(project.status)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressColor(project.progress)} transition-all`}
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-slate-600 min-w-[80px] text-right">
                      {project.uploaded_documents}/{project.total_documents} ({project.progress}%)
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-slate-500">
                <FolderOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>Δεν υπάρχουν ανατεθειμένα έργα υλοποίησης</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberStatistics;
